import { HHChip } from '@hinge-health/react-component-library';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { FeeScheduleEntityType } from '../../types';
import { formatCurrency } from '../../utils/currency-helpers';
import { FeeScheduleRow } from './use-fee-schedule-queries';

interface UseFeeScheduleColumnsProps {
  entityType: FeeScheduleEntityType;
  onEdit: (row: FeeScheduleRow) => void;
  onDelete: (row: FeeScheduleRow) => void;
  canWrite: boolean;
}

export const useFeeScheduleColumns = ({
  entityType,
  onEdit,
  onDelete,
  canWrite,
}: UseFeeScheduleColumnsProps): GridColDef[] =>
  useMemo(
    () => [
      {
        field: 'entityId',
        headerName:
          entityType === FeeScheduleEntityType.Client
            ? 'Client ID'
            : 'Partnership ID',
        flex: 0.5,
        minWidth: 80,
        valueFormatter: (params): string => String(params.value),
      },
      {
        field: 'entityName',
        headerName:
          entityType === FeeScheduleEntityType.Client
            ? 'Client Name'
            : 'Partnership Name',
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: 'paymentType',
        headerName: 'Payment Type',
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'price',
        headerName: 'Price',
        flex: 0.7,
        minWidth: 100,
        renderCell: (params: GridRenderCellParams<FeeScheduleRow>): string => {
          const rawPrice = params.row.price;
          const numValue = Number(rawPrice);
          return isNaN(numValue) ? '--' : formatCurrency(numValue, 'USD', true);
        },
      },
      {
        field: 'paymentSystem',
        headerName: 'Payment System',
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'notes',
        headerName: 'Notes',
        flex: 1.5,
        minWidth: 150,
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        flex: 0.6,
        minWidth: 100,
        renderCell: (params): JSX.Element => (
          <HHChip
            hhVariant="filled"
            label={params.value ? 'Yes' : 'No'}
            color={params.value ? 'primary' : 'default'}
            size="small"
          />
        ),
      },
      ...(canWrite
        ? [
            {
              field: 'actions',
              headerName: 'Actions',
              type: 'actions',
              width: 100,
              getActions: (
                params: GridRowParams<FeeScheduleRow>,
              ): JSX.Element[] => [
                <GridActionsCellItem
                  key={`edit-${params.id}`}
                  icon={<EditIcon />}
                  label="Edit"
                  onClick={(): void => onEdit(params.row)}
                />,
                <GridActionsCellItem
                  key={`delete-${params.id}`}
                  icon={<DeleteOutlineIcon />}
                  label="Delete"
                  onClick={(): void => onDelete(params.row)}
                />,
              ],
            },
          ]
        : []),
    ],
    [entityType, onEdit, onDelete, canWrite],
  );
